import { getKiosks } from 'admin/actions/devices';
import { getPrivateMoneys } from 'admin/actions/private-moneys';
import { Button } from 'admin/components/atoms/Buttons';
import Select from 'admin/components/atoms/Select';
import Table from 'admin/components/atoms/Table';
import LabeledSelect from 'admin/components/molecules/LabeledSelect';
import LabeledTextBox from 'admin/components/molecules/LabeledTextBox';
import Pager from 'admin/components/shared/Pager';
import { formatNum, toQueryParams } from 'admin/utils';
import PropTypes from 'prop-types';
import React, { useEffect, useRef, useState } from 'react';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';
import styled from 'styled-components';

const FormWrapper = styled.div``;

const FilterFormWrapper = styled.div`
  display: flex;

  ${Select} {
    width: 300px;
  }
  ${LabeledSelect} {
    margin: 5px;
  }
  ${LabeledTextBox} {
    margin: 5px;
  }
  ${Button} {
    margin: 5px;
  }
`;

const Kiosks = ({ user }) => {
  const defaultMoney = user.defaultMoney();
  const baseURL = '/settings/devices/kiosk';
  const defaultPagination = {
    current: 1,
    per_page: 20,
    max_page: 1,
    has_prev: false,
    has_next: false,
  };
  const healthOptions = [
    { label: '死活状態を選択してください', value: null },
    { label: 'OK', value: true },
    { label: 'NG', value: false },
  ];

  const [kiosks, setKiosks] = useState([]);
  const [query, setQuery] = useState({
    privateMoneyId: defaultMoney.id,
    terminalName: '',
    shopName: '',
  });
  const [privateMoneys, setPrivateMoneys] = useState([]);
  const [selectedMoney, setSelectedMoney] = useState({
    label: defaultMoney.name,
    value: defaultMoney,
  });
  const [health, setHealth] = useState(healthOptions[0]);

  const terminalNameField = useRef();
  const shopNameField = useRef();

  const setMoneys = (moneys) => {
    const ops = moneys.map((m) => ({ label: m.name, value: m }));
    setPrivateMoneys(ops);
  };

  const clearTextField = () => {
    terminalNameField.current.value = '';
    shopNameField.current.value = '';
  };

  const fetchKiosks = (pagination) => {
    const pageParams = Object.assign({}, { page: 1, per_page: 50 }, pagination);
    const params = Object.assign({}, query, pageParams);
    getKiosks(toQueryParams(params, true)).then((ks) => {
      setKiosks(ks);
    });
  };

  const fetchKiosksByChangeMoney = (moneyId) => {
    const params = {
      privateMoneyId: moneyId,
      terminalName: '',
      shopName: '',
      page: 1,
      per_page: 50,
    };
    getKiosks(toQueryParams(params, true)).then((ks) => {
      setKiosks(ks);
    });
  };

  const onSearch = () => {
    fetchKiosks();
  };

  const onChangePage = (page) => {
    fetchKiosks({ page });
  };

  const onChangeForm = (key, value) => {
    const updateObj = Object.assign(query, { [key]: value });
    setQuery(updateObj);
  };

  const clearQuery = (moneyId) => {
    setQuery({
      privateMoneyId: moneyId,
      terminalName: '',
      shopName: '',
    });
  };

  const onClear = () => {
    setSelectedMoney({
      label: defaultMoney.name,
      value: defaultMoney,
    });
    clearTextField();
    clearQuery(defaultMoney.id);
    fetchKiosksByChangeMoney(defaultMoney.id);
  };

  const onSelectMoney = (op) => {
    const moneyId = op.value.id;
    setSelectedMoney(op);
    clearTextField();
    clearQuery(moneyId);
    fetchKiosksByChangeMoney(moneyId);
    setHealth(healthOptions[0]);
  };

  useEffect(() => {
    fetchKiosks();
    if (user.isServiceUser()) {
      getPrivateMoneys('per_page=2000').then((moneys) => {
        setMoneys(moneys.rows);
      });
    } else {
      setMoneys(user.issuedMoneys());
    }
  }, []);

  const columns = [
    { label: 'ID', size: 1 },
    { label: '名前', size: 3 },
    { label: '死活', size: 1 },
    { label: '合計金額', size: 2 },
    { label: '合計枚数(硬貨)', size: 1.5 },
    { label: '合計枚数(紙幣)', size: 1.5 },
    { label: '各種履歴', size: 3 },
  ];

  const isHealthCheck = (is_network_alive, latest_health) =>
    is_network_alive &&
    latest_health.coinmech === 'OK' &&
    latest_health.bill_validator === 'OK' &&
    latest_health.printer === 'OK' &&
    latest_health.escrow === 'OK' &&
    latest_health.front_door === 'OK' &&
    latest_health.ups === 'OK';

  const makeHistoryLink = (kiosk, type, label) => (
    <div>
      <Link to={`${baseURL}/${type}?kiosk_id=${kiosk.id}&kiosk_name=${kiosk.name}`}>{label}</Link>
    </div>
  );

  const onHealthChange = (healthObj) => {
    setHealth(healthObj);
    const params = Object.assign({}, query, { page: 1, per_page: 50 });
    getKiosks(toQueryParams(params, true)).then((ks) => {
      if (healthObj.value !== null) {
        const rows = {
          rows: ks.rows
            .map((k) =>
              isHealthCheck(k.is_network_alive, k.latest_health) === healthObj.value ? k : null
            )
            .filter((v) => v),
        };
        setKiosks(Object.assign(ks, rows));
      } else {
        setKiosks(ks);
      }
    });
  };

  const makeFilterForm = () => (
    <FilterFormWrapper>
      <LabeledSelect
        labelText="マネー"
        options={privateMoneys}
        value={selectedMoney}
        onChange={(v) => onSelectMoney(v)}
        placeholder="マネーを選択してください。"
      />
      <LabeledSelect
        labelText="死活"
        options={healthOptions}
        value={health}
        onChange={onHealthChange}
      />
      <LabeledTextBox
        labelText="端末名"
        onBlur={(v) => onChangeForm('terminalName', v.target.value)}
        innerRef={terminalNameField}
      />
      <LabeledTextBox
        labelText="設置店舗名"
        onBlur={(v) => onChangeForm('shopName', v.target.value)}
        innerRef={shopNameField}
      />
      <Button onClick={onClear}>クリア</Button>
      <Button onClick={onSearch}>絞り込む</Button>
    </FilterFormWrapper>
  );

  return (
    <FormWrapper>
      {makeFilterForm()}
      <Table>
        <Table.Header>
          <Table.Row>
            {columns.map((c) => (
              <Table.Cell size={c.size} key={c.label} align="center">
                {c.label}
              </Table.Cell>
            ))}
          </Table.Row>
        </Table.Header>
        <Table.Body>
          {kiosks?.rows?.map((kiosk) => (
            <Table.Row key={kiosk.id}>
              <Table.Cell align="center">{kiosk.id}</Table.Cell>
              <Table.Cell align="center">
                <Link to={`${baseURL}/${kiosk.id}`}>{kiosk.name}</Link>
              </Table.Cell>
              <Table.Cell align="center">
                {kiosk.latest_health !== undefined ? (
                  isHealthCheck(kiosk.is_network_alive, kiosk.latest_health) ? (
                    <div style={{ color: 'green' }}>OK</div>
                  ) : (
                    <div style={{ color: 'red' }}>NG</div>
                  )
                ) : null}
              </Table.Cell>
              <Table.Cell align="center">¥ {formatNum(kiosk.total_amount)}</Table.Cell>
              <Table.Cell align="center">{kiosk.coin_count}</Table.Cell>
              <Table.Cell align="center">{kiosk.bill_count}</Table.Cell>
              <Table.Cell align="center">
                {makeHistoryLink(kiosk, 'plays', 'チャージ履歴')}
                {makeHistoryLink(kiosk, 'maintenances', 'メンテナンス履歴')}
                {makeHistoryLink(kiosk, 'health', 'ヘルス履歴')}
                {makeHistoryLink(kiosk, 'cash-collect', '現金回収履歴')}
              </Table.Cell>
            </Table.Row>
          ))}
        </Table.Body>
        <Table.Footer>
          <Table.Row noBorder>
            <Table.Cell colSpan="7" align="center" styles={{ paddingTop: '16px' }}>
              <Pager
                pagination={kiosks.pagination !== undefined ? kiosks.pagination : defaultPagination}
                onChangePage={onChangePage}
              />
            </Table.Cell>
          </Table.Row>
        </Table.Footer>
      </Table>
    </FormWrapper>
  );
};

Kiosks.propTypes = {
  user: PropTypes.object.isRequired,
};

const mapStateToProps = (state) => ({
  user: state.user,
});

export default connect(mapStateToProps)(Kiosks);
